import React, { useEffect, useState } from 'react';
import _ from 'lodash';
import { IEvents, IEventsState, EventStats, LineChartTypes } from 'types';
import { connect } from 'react-redux';
import moment from 'moment';
import { IRootState } from 'store';
import { LineChartGradients } from 'utils/helpers/lineChart';
import LineChartElement from './lineChartElement';
import './index.scss';
import AttendanceChartElement from './attendanceChartElement';

const mapStateToProps = (state: IRootState) => ({
  events: state.events,
});

interface LineChartProps {
  events: IEventsState;
  selectedDate: Date;
  title?: string;
  styles?: React.CSSProperties;
  type: LineChartTypes;
}

const LineChart: React.FC<LineChartProps> = ({
  events,
  selectedDate,
  title,
  styles,
  type,
}) => {
  const [days, setDays] = useState<string[]>([]);
  const [eventsList, setEventsList] = useState<EventStats[]>([]);
  const [totalLeftCell, setTotalLeftCell] = useState<number>(0);
  const [totalRightCell, setTotalRightCell] = useState<number>(0);

  useEffect(() => {
    const daysArr = [];
    const currentDate = moment(selectedDate) || moment();
    const weekStart = currentDate.clone().weekday(1);

    for (let i = 0; i <= 6; i += 1) {
      daysArr.push(
        moment(weekStart).subtract(1, 'day').add(i, 'days').format('DD-MM-YYYY')
      );
    }
    setDays(daysArr);
  }, [events.eventsList, selectedDate]);

  useEffect(() => {
    const eventsData =
      (!_.isEmpty(events.eventsList) &&
        events.eventsList.map((e: IEvents) => {
          const duration = moment(e.endTime, 'YYYY-MM-DD HH:mm').diff(
            moment(e.startTime, 'YYYY-MM-DD HH:mm'),
            'minutes'
          );
          const online = !!e.link;
          return {
            date: e.startTime,
            duration,
            isOnline: online,
            id: e.id,
            attended: e.isStarted,
          };
        })) ||
      [];
    const online = _.sumBy(eventsData, (o: EventStats) =>
      o.isOnline ? o.duration : 0
    );
    const offline = _.sumBy(eventsData, (o: EventStats) =>
      !o.isOnline ? o.duration : 0
    );
    setTotalRightCell(online);
    setTotalLeftCell(offline);
    setEventsList(eventsData);
  }, [events]);

  const convertMinsToHrsMins = (mins: number) => {
    const h: number = Math.floor(mins / 60);
    const m: number = mins % 60;
    return `${h}.${m}h`;
  };

  const attendance = type === LineChartTypes.Attendance;

  const getEventsQuantity = () => {
    const maxEventsLength = 12;
    return (
      <div className="lineChart__eventsQuantity">
        {[...Array(maxEventsLength)].map((_e, i) => (
          <span className="lineChart__eventsQuantity__item">{i + 1}</span>
        ))}
      </div>
    );
  };

  return (
    <div className="lineChart" style={styles && styles}>
      <div className="lineChart__title">
        <div className="title__content">{title || 'Weekly time schedule'}</div>
      </div>
      <div className="lineChart__table">
        <div className="table__status">
          <div className="status__item">
            <div
              className="item__circle"
              style={{
                backgroundImage: LineChartGradients[type].left,
              }}
            />
            <span
              className="status__item__text"
              style={{
                backgroundImage: LineChartGradients[type].left,
              }}
            >
              {!attendance ? '- offline' : '- attended'}
            </span>
          </div>
          <div className="status__item">
            <div
              className="item__circle"
              style={{
                backgroundImage: LineChartGradients[type].right,
              }}
            />
            <span
              className="status__item__text"
              style={{
                backgroundImage: LineChartGradients[type].right,
              }}
            >
              {!attendance ? '- online' : '- missed'}
            </span>
          </div>
        </div>
        {!_.isEmpty(days) &&
          days.map((event: string, i: number) => {
            const dailyEvents: EventStats[] = [];
            // eslint-disable-next-line no-unused-expressions
            !_.isEmpty(eventsList) &&
              eventsList.forEach((s: EventStats) => {
                if (moment(s.date).format('DD-MM-YYYY') === event) {
                  dailyEvents.push(s);
                }
                return null;
              });

            if (type === LineChartTypes.Attendance) {
              return (
                <AttendanceChartElement
                  days={days}
                  idx={i}
                  stats={dailyEvents}
                  key={event}
                />
              );
            }

            return (
              <LineChartElement
                days={days}
                idx={i}
                lineProps={dailyEvents}
                convertMinsToHrsMins={convertMinsToHrsMins}
                key={event}
              />
            );
          })}
      </div>
      {!attendance && (
        <div className="lineChart__description">
          <div className="description__content">
            <div className="content__item">Week</div>
            <div
              className="content__item line"
              style={{
                backgroundImage: !attendance
                  ? 'linear-gradient(360deg, #F5781E 0%, #FFC148 100%)'
                  : 'linear-gradient(180deg, #17ECDF 0%, #0B8E86 100%)',
              }}
            >
              {' '}
              {convertMinsToHrsMins(totalLeftCell)}
            </div>
            <div
              className="content__item line"
              style={{
                backgroundImage: !attendance
                  ? 'linear-gradient(180deg, #12BBF0 0%, #5348DA 100%)'
                  : 'linear-gradient(360deg, #E5225C 0%, #FB61A2 100%)',
              }}
            >
              {' '}
              {convertMinsToHrsMins(totalRightCell)}
            </div>
            <div className="content__item">
              {convertMinsToHrsMins(totalRightCell + totalLeftCell)}
            </div>
          </div>
        </div>
      )}
      {attendance && getEventsQuantity()}
    </div>
  );
};

export default connect(mapStateToProps)(LineChart);
